// @flow
import { type ActionType, createAction, handleActions } from 'redux-actions';
import { applyPenders, type GenericResponseAction } from 'lib/common';
import produce from 'immer';
import * as CommonAPI from 'lib/api/common';
import * as PostsAPI from 'lib/api/posts';
import * as ProductsAPI from 'lib/api/products';
import * as BannersAPI from 'lib/api/banners';
import * as BrandsAPI from 'lib/api/brands';
import * as EventsAPI from 'lib/api/events';
import * as LettersAPI from 'lib/api/letters';
import * as QuotesAPI from 'lib/api/quotes';
import * as PartnersAPI from 'lib/api/partners';
import { type Location } from 'react-router-dom';

const GET_TAGS = 'common/GET_TAGS';
const SET_TAG_INFO = 'common/SET_TAG_INFO';
const GET_TAG_INFO = 'common/GET_TAG_INFO';

const GET_SECTIONS = 'common/GET_SECTIONS';
const SET_SECTION_INFO = 'common/SET_SECTION_INFO';
const GET_SECTION_INFO = 'common/GET_SECTION_INFO';

const GET_PRODUCTS_TAGS = 'common/GET_PRODUCTS_TAGS';
const SET_PRODUCTS_TAG_INFO = 'common/SET_PRODUCTS_TAG_INFO';
const GET_PRODUCTS_TAG_INFO = 'common/GET_PRODUCTS_TAG_INFO';

const GET_CATEGORIES = 'common/GET_CATEGORIES';
const SET_CATEGORY_INFO = 'common/SET_CATEGORY_INFO';
const GET_CATEGORY_INFO = 'common/GET_CATEGORY_INFO';

const ASK_REMOVE = 'common/saves/ASK_REMOVE';
const CLOSE_REMOVE = 'common/saves/CLOSE_REMOVE';
const REMOVE_POST = 'common/saves/REMOVE_POST';
const REMOVE_PRODUCT = 'common/saves/REMOVE_PRODUCT';
const REMOVE_BANNER = 'common/saves/REMOVE_BANNER';
const REMOVE_BRAND = 'common/saves/REMOVE_BRAND';
const REMOVE_EVENT = 'common/saves/REMOVE_EVENT';
const REMOVE_LETTER = 'common/saves/REMOVE_LETTER';
const REMOVE_QUOTE = 'common/saves/REMOVE_QUOTE';
const REMOVE_PARTNER = 'common/saves/REMOVE_PARTNER';
const CHANGE_ROUTE = 'common/router/CHANGE_ROUTE';
const DID_SSR = 'common/DID_SSR';

export type TagData = {
  name: string,
  products_count: number,
  posts_count: number,
};

export type SectionData = {
  id: string,
  title: string,
  ids: any,
  type: string,
  description: string,
  sequence: number,
  limit: number,
  keyword: any,
  thumbnail: string,
  url_slug: string,
  is_privat: boolean,
  created_at: any,
  update: any,
  products: any
};

export type ProductsTagData = {
  name: string,
  products_count: number,
  posts_count: number,
};

export type CategoryData = {
  id: string,
  name: string,
  products_count: number,
  parent: any,
  order: any,
};

export type HistoryPayload = Location & { type: string };

export const actionCreators = {
  getTags: createAction(GET_TAGS, CommonAPI.getTags, meta => meta),
  setTagInfo: createAction(SET_TAG_INFO, (info: ?TagData) => info),
  getTagInfo: createAction(GET_TAG_INFO, CommonAPI.getTagInfo),
  getSections: createAction(GET_SECTIONS, CommonAPI.getSections),
  setSectionInfo: createAction(SET_SECTION_INFO, (info: ?SectionData) => info),
  getSectionInfo: createAction(GET_SECTION_INFO, CommonAPI.getSectionInfo),
  getProductsTags: createAction(GET_PRODUCTS_TAGS, CommonAPI.getProductsTags, meta => meta),
  setProductsTagInfo: createAction(SET_PRODUCTS_TAG_INFO, (info: ?ProductsTagData) => info),
  getProductsTagInfo: createAction(GET_PRODUCTS_TAG_INFO, CommonAPI.getProductsTagInfo),
  getCategories: createAction(GET_CATEGORIES, CommonAPI.getCategories, meta => meta),
  setCategoryInfo: createAction(SET_CATEGORY_INFO, (info: ?CategoryData) => info),
  getCategoryInfo: createAction(GET_CATEGORY_INFO, CommonAPI.getCategoryInfo),
  askRemove: createAction(ASK_REMOVE, (postId: string) => postId),
  closeRemove: createAction(CLOSE_REMOVE),
  removePost: createAction(REMOVE_POST, PostsAPI.deletePost),
  removeProduct: createAction(REMOVE_PRODUCT, ProductsAPI.deleteProduct),
  removeBanner: createAction(REMOVE_BANNER, BannersAPI.deleteBanner),
  removeBrand: createAction(REMOVE_BRAND, BrandsAPI.deleteBrand),
  removeEvent: createAction(REMOVE_EVENT, EventsAPI.deleteEvent),
  removeLetter: createAction(REMOVE_LETTER, LettersAPI.deleteLetter),
  removeQuote: createAction(REMOVE_QUOTE, QuotesAPI.deleteQuote),
  removePartner: createAction(REMOVE_PARTNER, PartnersAPI.deletePartner),
  changeRoute: createAction(CHANGE_ROUTE, (payload: HistoryPayload) => payload),
  didSSR: createAction(DID_SSR),
};

type GetTagsResponseAction = GenericResponseAction<TagData[], string>;
type GetTagInfoResponseAction = GenericResponseAction<TagData, string>;
type SetTagInfoAction = ActionType<typeof actionCreators.setTagInfo>;

type GetSectionsResponseAction = GenericResponseAction<SectionData[], string>;
type GetSectionInfoResponseAction = GenericResponseAction<SectionData, string>;
type SetSectionInfoAction = ActionType<typeof actionCreators.setSectionInfo>;

type GetProductsTagsResponseAction = GenericResponseAction<ProductsTagData[], string>;
type GetProductsTagInfoResponseAction = GenericResponseAction<ProductsTagData, string>;
type SetProductsTagInfoAction = ActionType<typeof actionCreators.setProductsTagInfo>;

type GetCategoriesResponseAction = GenericResponseAction<CategoryData[], string>;
type GetCategoryInfoResponseAction = GenericResponseAction<CategoryData, string>;
type SetCategoryInfoAction = ActionType<typeof actionCreators.setCategoryInfo>;

type AskRemoveAction = ActionType<typeof actionCreators.askRemove>;
type ChangeRouteAction = ActionType<typeof actionCreators.changeRoute>;

export type CommonState = {
  tags: {
    data: ?(TagData[]),
    selected: ?TagData,
    lastParam: ?string,
    sort: string,
  },
  sections: {
    data: ?(SectionData[]),
    selected: ?SectionData,
  },
  productsTags: {
    data: ?(TagData[]),
    selected: ?TagData,
    lastParam: ?string,
    sort: string,
  },
  categories: {
    data: ?(CategoryData[]),
    selected: ?CategoryData,
    lastParam: ?string,
    sort: string,
  },
  saves: {
    removeId: ?string,
    ask: boolean,
  },
  router: {
    history: Location[],
    altered: boolean,
  },
  ssr: boolean,
};

const initialState: CommonState = {
  tags: {
    data: null,
    selected: null,
    lastParam: null,
    sort: 'popular',
  },
  sections: {
    data: null,
    selected: null,
  },
  productsTags: {
    data: null,
    selected: null,
    lastParam: null,
    sort: 'popular',
  },
  categories: {
    data: null,
    selected: null,
    lastParam: null,
    sort: 'popular',
  },
  saves: {
    removeId: null,
    ask: false,
  },
  router: {
    history: [],
    altered: false,
  },
  ssr: false,
};

const reducer = handleActions(
  {
    [SET_TAG_INFO]: (state, { payload }: SetTagInfoAction) => {
      return produce(state, draft => {
        draft.tags.selected = payload;
      });
    },
    [SET_SECTION_INFO]: (state, { payload }: SetSectionInfoAction) => {
      return produce(state, draft => {
        draft.sections.selected = payload;
      });
    },
    [SET_PRODUCTS_TAG_INFO]: (state, { payload }: SetProductsTagInfoAction) => {
      return produce(state, draft => {
        draft.productsTags.selected = payload;
      });
    },
    [SET_CATEGORY_INFO]: (state, { payload }: SetCategoryInfoAction) => {
      return produce(state, draft => {
        draft.categories.selected = payload;
      });
    },
    [ASK_REMOVE]: (state, { payload }: AskRemoveAction) => {
      return produce(state, draft => {
        draft.saves.ask = true;
        draft.saves.removeId = payload;
      });
    },
    [CLOSE_REMOVE]: state => {
      return produce(state, draft => {
        draft.saves.ask = false;
      });
    },
    [CHANGE_ROUTE]: (state, { payload }: ChangeRouteAction) => {
      return produce(state, draft => {
        const { type, ...rest } = payload;
        if (draft.router.history.length) draft.router.altered = true;
        if (type === 'PUSH') {
          draft.router.history.push(rest);
        }
        if (type === 'POP') {
          draft.router.history.pop();
        }
        if (type === 'REPLACE') {
          draft.router.history[draft.router.history.length - 1] = payload;
        }
      });
    },
    [DID_SSR]: state => ({
      ...state,
      ssr: true,
    }),
  },
  initialState,
);

export default applyPenders(reducer, [
  {
    type: GET_TAGS,
    onSuccess: (state: CommonState, { payload, meta }: GetTagsResponseAction) => {
      return produce(state, draft => {
        draft.tags.data = payload.data;
        draft.tags.sort = meta;
      });
    },
  },
  {
    type: GET_TAG_INFO,
    onPending: (state: CommonState, action) => {
      return produce(state, draft => {
        draft.tags.lastParam = action.meta;
      });
    },
    onSuccess: (state: CommonState, { payload }: GetTagInfoResponseAction) => {
      return produce(state, draft => {
        draft.tags.selected = payload.data;
      });
    },
  },
  {
    type: GET_SECTIONS,
    onSuccess: (state: CommonState, { payload }: GetSectionsResponseAction) => {
      return produce(state, draft => {
        draft.sections.data = payload.data;
      });
    },
  },
  {
    type: GET_SECTION_INFO,
    onSuccess: (state: CommonState, { payload }: GetSectionInfoResponseAction) => {
      return produce(state, draft => {
        draft.sections.selected = payload.data;
      });
    },
  },
  {
    type: GET_PRODUCTS_TAGS,
    onSuccess: (state: CommonState, { payload, meta }: GetProductsTagsResponseAction) => {
      return produce(state, draft => {
        draft.productsTags.data = payload.data;
        draft.productsTags.sort = meta;
      });
    },
  },
  {
    type: GET_PRODUCTS_TAG_INFO,
    onPending: (state: CommonState, action) => {
      return produce(state, draft => {
        draft.productsTags.lastParam = action.meta;
      });
    },
    onSuccess: (state: CommonState, { payload }: GetProductsTagInfoResponseAction) => {
      return produce(state, draft => {
        draft.productsTags.selected = payload.data;
      });
    },
  },
  {
    type: GET_CATEGORIES,
    onSuccess: (state: CommonState, { payload, meta }: GetCategoriesResponseAction) => {
      return produce(state, draft => {
        draft.categories.data = payload.data;
        draft.categories.sort = meta;
      });
    },
  },
  {
    type: GET_CATEGORY_INFO,
    onPending: (state: CommonState, action) => {
      return produce(state, draft => {
        draft.categories.lastParam = action.meta;
      });
    },
    onSuccess: (state: CommonState, { payload }: GetCategoryInfoResponseAction) => {
      return produce(state, draft => {
        draft.categories.selected = payload.data;
      });
    },
  },
]);



// WEBPACK FOOTER //
// ./src/store/modules/common.js